/** Per-source waste amounts shown on the waste-stream grids. */
export type WasteStreamAmounts = {
  vendorAmount?: number | null;
  eManifestAmount?: number | null;
  documentAmount?: number | null;
};

type AmountSource = "e-Manifest" | "Vendor" | "Document";

/**
 * Tooltip copy explaining the consolidated "Amount" value. Generic by design —
 * it conveys that the value is the maximum across all reported sources without
 * enumerating them, so it reads correctly regardless of which sources a tenant
 * has.
 */
export const WASTE_AMOUNT_TOOLTIP = "The highest amount reported across all sources.";

/**
 * The maximum amount across the available sources, plus which source it came
 * from (for the tooltip). Returns a null value when no source reported an
 * amount. Ties resolve to the first listed source (e-Manifest).
 */
export function getConsolidatedWasteAmount(amounts: WasteStreamAmounts): {
  value: number | null;
  source: AmountSource | null;
} {
  const sources: { label: AmountSource; amount: number }[] = [
    { label: "e-Manifest", amount: amounts.eManifestAmount ?? NaN },
    { label: "Vendor", amount: amounts.vendorAmount ?? NaN },
    { label: "Document", amount: amounts.documentAmount ?? NaN },
  ];
  const candidates = sources.filter((c) => Number.isFinite(c.amount));

  if (candidates.length === 0) return { value: null, source: null };

  const best = candidates.reduce((a, b) => (b.amount > a.amount ? b : a));
  return { value: best.amount, source: best.label };
}
